import { logError } from "utility/Function";
import type { TapInContext } from "apps/user/learner/lib/tap-in";

const STARTER_MESSAGE_KEY = "ori.starterMessage";
const LAST_STARTER_KEY = "ori.lastStarter";

const STARTER_COOLDOWN_MS = 30 * 60 * 1000;

// A stash is consumed only once the socket connects; if that never happens it
// survives in sessionStorage and would fire on an unrelated later connect.
// Drop stashes older than this so a starter fires only for the session that set it.
const STARTER_FRESHNESS_MS = 2 * 60 * 1000;

export interface OriStarter {
  text: string;
  entryPoint: string;
  /** Set by tap-in entry points: what the screen showed, for Coach. */
  context?: TapInContext;
}

// sessionStorage survives the navigation to the chat route but resets per tab.
export function setOriStarterMessage(
  text: string,
  entryPoint: string,
  context?: TapInContext
): void {
  sessionStorage.setItem(
    STARTER_MESSAGE_KEY,
    JSON.stringify({ text, entryPoint, context, setAt: Date.now() })
  );
}

// Stash only an entry point (no starter text) for surfaces that navigate to the
// chat without seeding a prompt, e.g. the nav tab.
export function setOriEntryPoint(entryPoint: string): void {
  sessionStorage.setItem(
    STARTER_MESSAGE_KEY,
    JSON.stringify({ text: "", entryPoint, setAt: Date.now() })
  );
}

// Re-stamp an existing stash so its freshness window restarts. First-time users
// can dwell on the Ori disclaimer longer than STARTER_FRESHNESS_MS before the
// socket connects; without this the stash would be dropped and they'd land in an
// empty chat. No-op when nothing is stashed.
export function refreshOriStarterMessage(): void {
  const raw = sessionStorage.getItem(STARTER_MESSAGE_KEY);
  if (raw === null) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    // Unparseable stash — leave it; consume will drop it.
    logError("Unparseable Ori starter stash on refresh", err as Error);
    return;
  }
  if (typeof parsed === "object" && parsed !== null && "text" in parsed) {
    sessionStorage.setItem(
      STARTER_MESSAGE_KEY,
      JSON.stringify({ ...parsed, setAt: Date.now() })
    );
  }
}

// Shape and freshness check shared by consume and peek; null for anything
// unparseable (e.g. a legacy bare string), malformed or stale.
function parseStash(raw: string): OriStarter | null {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null && "text" in parsed) {
      const setAt = Number(parsed.setAt);
      if (!setAt || Date.now() - setAt > STARTER_FRESHNESS_MS) return null;
      return {
        text: String(parsed.text ?? ""),
        entryPoint: String(parsed.entryPoint ?? "direct"),
        ...(parsed.context ? { context: parsed.context as TapInContext } : {}),
      };
    }
  } catch {
    // Unparseable — fall through and drop it.
  }
  return null;
}

// Read-and-clear so the prompt is sent at most once, even across a remount.
export function consumeOriStarterMessage(): OriStarter | null {
  const raw = sessionStorage.getItem(STARTER_MESSAGE_KEY);
  if (raw === null) return null;
  sessionStorage.removeItem(STARTER_MESSAGE_KEY);
  return parseStash(raw);
}

// True while a fresh stash with prompt text waits to be sent. Never consumes.
export function hasPendingOriStarterText(): boolean {
  const raw = sessionStorage.getItem(STARTER_MESSAGE_KEY);
  if (raw === null) return false;
  return (parseStash(raw)?.text.trim() ?? "") !== "";
}

// Persisted across tabs so a re-click in another tab is still throttled.
export function recordStarterSent(text: string): void {
  localStorage.setItem(
    LAST_STARTER_KEY,
    JSON.stringify({ text, at: Date.now() })
  );
}

// True when this exact prompt was auto-sent within the cooldown window, so the
// entry point should drop the user back into their chat instead of re-sending
// (repeat prompts make Ori respond as if nagged).
export function wasStarterSentRecently(text: string): boolean {
  const raw = localStorage.getItem(LAST_STARTER_KEY);
  if (raw === null) return false;
  const { text: lastText, at } = JSON.parse(raw);
  return lastText === text && Date.now() - at < STARTER_COOLDOWN_MS;
}
